/**
 * TanStack Query hooks for project member management.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchMembers,
  inviteMember,
  updateMemberRole,
  removeMember,
  acceptInvitation,
  transferOwnership,
  type ProjectMember,
  type InviteMemberBody,
} from "../api/members";

export function useMembers(projectId: string | undefined) {
  return useQuery<ProjectMember[]>({
    queryKey: ["members", projectId],
    queryFn: () => fetchMembers(projectId!),
    enabled: !!projectId,
  });
}

export function useInviteMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteMemberBody) => inviteMember(projectId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", projectId] }),
  });
}

export function useUpdateRole(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      updateMemberRole(projectId, memberId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", projectId] }),
  });
}

export function useRemoveMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => removeMember(projectId, memberId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", projectId] }),
  });
}

export function useAcceptInvitation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => acceptInvitation(projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", projectId] }),
  });
}

export function useTransferOwnership(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (newOwnerUserId: string) =>
      transferOwnership(projectId, newOwnerUserId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", projectId] }),
  });
}
